import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input, Label } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseOrders } from "../../helpers/orderEnabled";
import { getSalesMemo, getSalesReversalContext, takeBackSalesProduct } from "../../services/studioSalesApi";
import { unwrapGeoPayload } from "../gi-registry/geoPath";
import { parseStudioPieceId } from "./internalQr";
import { SalesScanIcons, SalesScanStage, useSalesScanner } from "./SalesScanPad";
import "../gi-registry/gi-registry.scss";
import "../orders/orders.scss";

const authRole = () => {
  try { return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role; }
  catch { return undefined; }
};

const SalesTakeBack = () => {
  const history = useHistory();
  const location = useLocation();
  const role = authRole();
  const canWrite = canUseOrders(role) && role !== "SUPER_ADMIN";
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<any>(null);
  const [context, setContext] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [corrected, setCorrected] = useState(false);
  const [correctionReference, setCorrectionReference] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!canUseOrders(role)) { history.replace("/dashboard"); return; }
    const params = new URLSearchParams(location.search);
    const memoId = params.get("memo");
    if (memoId) getSalesMemo(memoId).then((res: any) => {
      if (res?.success) setSource(unwrapGeoPayload(res) || res.data);
    });
  }, [history, location.search, role]);

  const check = async (value = query) => {
    const q = parseStudioPieceId(value) || value.trim();
    if (!q || !canWrite) return;
    setBusy(true);
    setContext(null);
    try {
      const res: any = await getSalesReversalContext(q);
      if (!res?.success) {
        notifications({ title: "Could not find sale", message: res?.message || "Check the piece ID", type: "danger" });
        return;
      }
      const next = unwrapGeoPayload(res) || res.data;
      if (source && next.sourceMemoId !== source.id) {
        notifications({ title: "Different sale", message: "Choose a piece from this billed memo", type: "danger" });
        return;
      }
      setQuery(q);
      setContext(next);
      setCorrected(false);
    } catch {
      notifications({ title: "Could not find sale", message: "Check your connection and try again", type: "danger" });
    } finally { setBusy(false); }
  };
  const scan = useSalesScanner((value) => { setQuery(value); return check(value); }, !canWrite || busy);

  useEffect(() => {
    const piece = new URLSearchParams(location.search).get("piece");
    if (piece && canWrite) {
      setQuery(piece);
      void check(piece);
    }
    // Load the piece named in the product-list link once per URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, canWrite]);

  const reverse = async () => {
    if (!context || !reason.trim() || (context.hasExternalInvoice && !corrected)) return;
    setBusy(true);
    try {
      const res: any = await takeBackSalesProduct({
        productQuery: context.productUniqueId,
        sourceOwnershipEventId: context.sourceOwnershipEventId,
        reason: reason.trim(),
        externalCorrectionConfirmed: corrected,
        externalCorrectionReference: correctionReference.trim() || undefined,
      });
      if (!res?.success) {
        notifications({ title: "Could not reverse sale", message: res?.message || "Try again", type: "danger" });
        return;
      }
      notifications({ title: "Sale reversed in HKP", message: "The original sale remains in movement history.", type: "success" });
      history.push("/sales");
    } catch {
      notifications({ title: "Could not reverse sale", message: "Check your connection and try again", type: "danger" });
    } finally { setBusy(false); }
  };

  return <div className="gi-registry-page sales-page">
    <div className="gi-toolbar-row"><h2>Wrong Bill / Take Back</h2></div>
    <p className="gi-au-hint">Use this for an incorrect bill, not a customer return. HKP does not cancel or correct the external invoice.</p>
    {source ? <div className="geo-card"><strong>{source.memoCode}</strong> · {source.invoiceNumber || "No invoice reference"}
      {(source.lines || []).filter((line: any) => line.lineStatus === "BILLED").map((line: any) =>
        <Button key={line.id} className="btn-hastkala-outline" disabled={busy}
          onClick={() => check(line.productUniqueId)}>{line.name || line.productUniqueId}</Button>)}</div> : null}
    <div className="gi-form-fields">
      <Label htmlFor="reverse-piece">Scan QR, HKP ID or NFC/XUID</Label>
      <Input id="reverse-piece" value={query} onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && check()} />
      <Button className="btn-hastkala-outline" disabled={!canWrite || busy || !query.trim()}
        onClick={() => check()}>Check sale</Button>
      <SalesScanIcons scan={scan} />
      <SalesScanStage scan={scan} />
    </div>
    {context ? <div className="geo-card gi-form-fields">
      <h3>{context.productName || context.productUniqueId}</h3>
      <p>Original sale: {context.sourceMemoCode} · Invoice: {context.invoiceNumber || "No reference"} · Party: {context.partyName || "—"}</p>
      <p>Ownership scope: {context.ownershipScope === "PRODUCT_AND_DESIGN" ? "Product + Design" : "Product only"}</p>
      <Label htmlFor="reverse-reason">Reason *</Label>
      <Input id="reverse-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      {context.hasExternalInvoice ? <Label className="sales-confirm-check"><Input type="checkbox" checked={corrected}
        onChange={(e) => setCorrected(e.target.checked)} />
        I confirm the external invoice has already been corrected or cancelled outside HKP.</Label> : null}
      <Label htmlFor="reverse-ref">External correction reference (optional)</Label>
      <Input id="reverse-ref" value={correctionReference} onChange={(e) => setCorrectionReference(e.target.value)} />
      <Button color="primary" disabled={busy || !reason.trim() || (Boolean(context.hasExternalInvoice) && !corrected)}
        onClick={reverse}>Reverse in HKP</Button>
    </div> : null}
  </div>;
};

export default SalesTakeBack;
